// The dashboard's response-volume trend: a single teal Area over time, or —
// once the page's split-by-placement toggle is on — one Line per placement
// (categorical --chart-series-* tokens, app.css). The toggle itself lives
// here so the card owns its own label, but the actual refetch (splitByPlacement=true
// on useStats) is the caller's: this component only renders whichever shape
// of `stats` it's handed (`trendByPlacement` present vs null). The server
// sends every bucket of the window, empty ones as 0, so the line drops to
// zero across a quiet stretch instead of sloping over it. It sits inside
// Results' Trend section card, which carries the frame and the title; the
// caption beside the switch names the bucket size. `ResponsesTrend` is also
// Overview's chart.

import { Switch } from "@pej/kore/components";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import type { SurveyStats } from "@/api/types";
import { bucketCaption, bucketLabel, hourTicks, weekTicks } from "@/features/dashboard/stats-mapping";

export interface TrendChartProps {
    stats: SurveyStats;
    splitByPlacement: boolean;
    onSplitByPlacementChange: (next: boolean) => void;
}

const SERIES_COLORS = [
    "var(--chart-series-1)",
    "var(--chart-series-2)",
    "var(--chart-series-3)",
    "var(--chart-series-4)",
    "var(--chart-series-5)",
];

const AXIS_TICK = { fill: "var(--color-muted-foreground)", fontSize: 11 };
const TOOLTIP_STYLE = {
    background: "var(--color-popover)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    fontSize: 12,
};

function EmptyTrend() {
    return (
        <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No responses in this period.
        </p>
    );
}

export type TrendUnit = SurveyStats["bucket"];

/** The x axis both charts share. An hour axis gets explicit ticks on whole
 *  steps (midnight among them), and its first tick names the date too; a
 *  week axis gets one tick per month, so no two read the same. */
function bucketAxis(buckets: string[], unit: TrendUnit) {
    const ticks = unit === "hour" ? hourTicks(buckets) : unit === "week" ? weekTicks(buckets) : undefined;
    return (
        <XAxis
            dataKey="bucket"
            ticks={ticks}
            tickFormatter={(value) => bucketLabel(String(value), unit, "tick", ticks?.[0] === value)}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: "var(--color-border)" }}
            minTickGap={32}
        />
    );
}

/** Every bucket arrives, empty or not: a window with no responses is all zeros. */
function isEmpty(counts: number[]): boolean {
    return counts.every((count) => count === 0);
}

export function ResponsesTrend({
    points,
    unit,
}: {
    points: readonly { bucket: string; count: number }[];
    unit: TrendUnit;
}) {
    if (isEmpty(points.map((point) => point.count))) return <EmptyTrend />;
    const buckets = points.map((point) => point.bucket);
    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={[...points]}>
                <CartesianGrid stroke="var(--color-border)" vertical={false} />
                {bucketAxis(buckets, unit)}
                <YAxis width={32} tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                    labelFormatter={(value) => bucketLabel(String(value), unit, "tooltip")}
                    contentStyle={TOOLTIP_STYLE}
                    isAnimationActive={false}
                />
                <Area
                    dataKey="count"
                    name="Responses"
                    stroke="var(--chart-series-1)"
                    fill="var(--chart-series-1)"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    isAnimationActive={false}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}

type PlacementSeries = NonNullable<SurveyStats["trendByPlacement"]>;

/** One row per bucket, each placement's count under its own placementId key
 *  — the shape recharts' multi-series LineChart wants. Every placement
 *  carries every bucket (0 where it had none), so no row misses a key. */
function mergeByBucket(series: PlacementSeries): Record<string, string | number>[] {
    const rows = new Map<string, Record<string, string | number>>();
    for (const placement of series) {
        for (const point of placement.points) {
            const row = rows.get(point.bucket) ?? { bucket: point.bucket };
            row[placement.placementId] = point.count;
            rows.set(point.bucket, row);
        }
    }
    return Array.from(rows.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
}

function SplitTrend({ series, unit }: { series: PlacementSeries; unit: TrendUnit }) {
    const data = mergeByBucket(series);
    if (isEmpty(series.flatMap((placement) => placement.points.map((point) => point.count)))) return <EmptyTrend />;
    const buckets = data.map((row) => String(row.bucket));
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
                <CartesianGrid stroke="var(--color-border)" vertical={false} />
                {bucketAxis(buckets, unit)}
                <YAxis width={32} tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                    labelFormatter={(value) => bucketLabel(String(value), unit, "tooltip")}
                    contentStyle={TOOLTIP_STYLE}
                    isAnimationActive={false}
                />
                <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} />
                {series.map((placement, index) => (
                    <Line
                        key={placement.placementId}
                        dataKey={placement.placementId}
                        name={placement.placementName}
                        stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

export function TrendChart({ stats, splitByPlacement, onSplitByPlacementChange }: TrendChartProps) {
    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">{bucketCaption(stats.bucket)}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <label htmlFor="trend-split-by-placement">Split by placement</label>
                    <Switch
                        id="trend-split-by-placement"
                        checked={splitByPlacement}
                        onCheckedChange={onSplitByPlacementChange}
                    />
                </div>
            </div>
            <div className="mt-4 h-72">
                {stats.trendByPlacement ? (
                    <SplitTrend series={stats.trendByPlacement} unit={stats.bucket} />
                ) : (
                    <ResponsesTrend points={stats.trend} unit={stats.bucket} />
                )}
            </div>
        </div>
    );
}
